import Tabs, { TabsProps } from "components/basic/tabs";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconButton, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import LeadTable from "./leadsTable";
import { useMessageNavigation } from "../message/messagesApi";
import { getStageLabelFromId, getAssigneeLabelFromId } from "./constants";
import {
  QuoteApprovalStatus,
  useGetLeadsByVendorQuery,
  useRenewQuoteMutation,
  useResendClientLinkMutation,
  useUpdateQuoteMutation,
} from "generated/graphql";
import { useSnack } from "hooks/useSnack";
import logger from "utils/logger";
import { useQuoteCreated } from "hooks/useQuoteCreated";
import { useQuoteUpdated } from "hooks/useQuoteUpdated";
import useUrlParamState from "hooks/useUrlParamState";
import Button from "components/basic/button";
import Badge from "components/basic/badge";
import Typography from "components/basic/typography";
import Quote from "pages/chargerEstimator/formPages/quote";
import {
  buildQuotePageDataFromQuote,
  QuoteCustomerContact,
} from "pages/chargerEstimator/formPages/quote/quoteTools";
import { quoteStatusMeta } from "../quotes/quoteStatus";
import ReviseQuoteModal from "./components/ReviseQuoteModal";
import {
  EMPTY_TAB_TEXT,
  isLeadTab,
  isOnTab,
  LEAD_TABS,
  LeadTab,
} from "./helpers";

export interface Lead {
  id: number;
  customerId: number;
  name: string;
  address: string;
  distance: number;
  createdAt: string;
  stage: string;
  assignedTo: string;
  expiresAt?: string | null;
  isExpired: boolean;
  approvalStatus: QuoteApprovalStatus;
  starred: boolean;
  archived: boolean;
  viewed: boolean;
}

type LeadEdit = Partial<
  Pick<Lead, "stage" | "assignedTo" | "starred" | "archived" | "viewed">
>;

const Leads = () => {
  const theme = useTheme();
  const { t } = useTranslation("leads");
  const { t: tQuotes } = useTranslation("quotes");
  const [tabParam, setTabParam] = useUrlParamState("tab", "all");
  const selectedTab: LeadTab = isLeadTab(tabParam) ? tabParam : "all";
  const [{ data }, refetchLeads] = useGetLeadsByVendorQuery({
    requestPolicy: "cache-and-network",
  });
  useQuoteCreated(refetchLeads);
  useQuoteUpdated(refetchLeads);
  const [, updateQuote] = useUpdateQuoteMutation();
  const [, renewQuote] = useRenewQuoteMutation();
  const [{ fetching: resendingLink }, resendClientLink] =
    useResendClientLinkMutation();
  const showSnack = useSnack();
  const handleMessage = useMessageNavigation();
  const [selectedLeadId, setSelectedLeadId] = useUrlParamState("leadId");
  const [revising, setRevising] = useState(false);

  const rawLeads = useMemo(() => data?.getLeadsByVendor ?? [], [data]);

  const leadsData: Lead[] = useMemo(() => {
    if (!rawLeads) return [];
    return rawLeads.map((quote) => ({
      id: quote.id,
      customerId: quote.chargerServiceRequest.account.id,
      name: `${quote.chargerServiceRequest.account.firstName} ${quote.chargerServiceRequest.account.lastName}`,
      address: quote.chargerServiceRequest.address,
      distance: parseFloat(quote.distance) || 0,
      createdAt: quote.chargerServiceRequest.createdAt,
      stage: quote.stage ?? "New",
      assignedTo: quote.assignedTo ?? "Unassigned",
      expiresAt: quote.expiresAt,
      isExpired: quote.isExpired,
      approvalStatus: quote.approvalStatus,
      starred: Boolean(quote.starred),
      archived: Boolean(quote.archived),
      viewed: Boolean(quote.viewed),
    }));
  }, [rawLeads]);

  logger.log("Fetched leads data:", rawLeads);

  const [pendingEdits, setPendingEdits] = useState<Record<number, LeadEdit>>(
    {},
  );

  const leads: Lead[] = useMemo(
    () =>
      leadsData.map((lead) =>
        pendingEdits[lead.id] ? { ...lead, ...pendingEdits[lead.id] } : lead,
      ),
    [leadsData, pendingEdits],
  );

  // Shown at once, then either confirmed by the server's copy or dropped.
  const applyEdit = useCallback(
    async (leadId: number, patch: LeadEdit) => {
      setPendingEdits((prev) => ({
        ...prev,
        [leadId]: { ...prev[leadId], ...patch },
      }));

      const result = await updateQuote({ id: leadId, ...patch });
      if (result.error || !result.data?.updateQuote) {
        showSnack(t("snack.update_failed"), "error");
      }

      setPendingEdits(({ [leadId]: _confirmed, ...rest }) => rest);
    },
    [updateQuote, showSnack, t],
  );

  const tabLeads = useMemo(
    () => leads.filter((lead) => isOnTab(lead, selectedTab)),
    [leads, selectedTab],
  );
  const unprocessedCount = useMemo(
    () => leads.filter((lead) => isOnTab(lead, "unprocessed")).length,
    [leads],
  );

  const toggleStar = (lead: Lead) =>
    applyEdit(lead.id, { starred: !lead.starred });
  const toggleArchive = (lead: Lead) => {
    applyEdit(lead.id, { archived: !lead.archived });
    showSnack(
      t(lead.archived ? "snack.restored" : "snack.archived"),
      "success",
    );
  };

  const selectedLead = useMemo(
    () =>
      selectedLeadId
        ? rawLeads.find((q) => `${q.id}` === selectedLeadId)
        : undefined,
    [selectedLeadId, rawLeads],
  );

  // A client this vendor's own portal created is the only kind with a sign-in
  // link to resend; everyone else has credentials of their own.
  const isPrivateClient =
    selectedLead?.chargerServiceRequest.account.invitedByVendor?.id ===
    selectedLead?.evChargerService.vendor.id;

  // Build read-only Quote page data for the selected lead
  const quoteDataForDetail = useMemo(() => {
    if (!selectedLeadId) {
      return null;
    }
    const record = rawLeads.find((q) => `${q.id}` === selectedLeadId);
    if (!record) {
      return null;
    }
    // Build customer contact from the lead's account
    const customerContact: QuoteCustomerContact = {
      fullName: `${record.chargerServiceRequest.account.firstName} ${record.chargerServiceRequest.account.lastName}`,
      email: record.chargerServiceRequest.account.email,
      phoneNumber: record.chargerServiceRequest.account.phoneNumber,
    };
    return buildQuotePageDataFromQuote(record, customerContact);
  }, [selectedLeadId, rawLeads]);

  const handleStageChange = (leadId: string | number, newStageId: number) => {
    applyEdit(leadId as number, { stage: getStageLabelFromId(newStageId) });
  };

  const handleAssigneeChange = (
    leadId: string | number,
    newAssigneeId: number,
  ) => {
    applyEdit(leadId as number, {
      assignedTo: getAssigneeLabelFromId(newAssigneeId),
    });
  };

  // Extends the quote by this service's current "Quote Valid For" from today,
  // making it bookable for the customer again.
  const handleRenew = async (leadId: number) => {
    const result = await renewQuote({ id: leadId });

    if (result.data?.renewQuote) {
      showSnack(t("snack.renewed"), "success");
      refetchLeads({ requestPolicy: "network-only" });
    } else {
      showSnack(t("snack.renew_failed"), "error");
    }
  };

  // Mails the client a fresh magic link. The previous one stops working, which
  // is the point: the reason to resend is that it never arrived.
  const handleResendLink = async (leadId: number) => {
    const result = await resendClientLink({ quoteId: leadId });
    const response = result.data?.resendClientLink;

    if (response?.sent) {
      showSnack(t("snack.link_sent"), "success");
      return;
    }

    showSnack(
      response?.errors?.[0]?.message ?? t("snack.link_failed"),
      "error",
    );
  };

  const tabData: TabsProps = {
    selectedTab,
    setSelectedTab: setTabParam,
    tabs: LEAD_TABS.map((tab) => ({
      ...tab,
      label:
        tab.path === "unprocessed" && unprocessedCount
          ? `${t(tab.label)} (${unprocessedCount})`
          : t(tab.label),
    })),
  };

  const detailLead = leads.find((lead) => `${lead.id}` === selectedLeadId);

  // Opening a lead is what takes it off the Unprocessed tab. Tried once per
  // lead, so a failed save can't retry on every render.
  const markedViewed = useRef(new Set<number>());
  useEffect(() => {
    if (
      detailLead &&
      !detailLead.viewed &&
      !markedViewed.current.has(detailLead.id)
    ) {
      markedViewed.current.add(detailLead.id);
      applyEdit(detailLead.id, { viewed: true });
    }
  }, [detailLead, applyEdit]);

  const leadStatus =
    selectedLead && quoteStatusMeta(selectedLead.approvalStatus, "vendor");

  // Read-only detail view for a selected lead
  if (selectedLeadId && quoteDataForDetail) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: theme.spacing(1),
          }}
        >
          <Button
            variant="text"
            startAdornment={<ArrowBackIcon />}
            onClick={() => {
              // Leaving the lead must also drop the editor, or the next lead
              // opened would come up with the modal already on top of it.
              setRevising(false);
              setSelectedLeadId(null);
            }}
          >
            {t("actions.back")}
          </Button>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(1),
            }}
          >
            {leadStatus && (
              <Badge
                label={tQuotes(leadStatus.label)}
                color={leadStatus.color}
              />
            )}

            {isPrivateClient && selectedLead && (
              <Button
                variant="outlined"
                disabled={resendingLink}
                onClick={() => handleResendLink(selectedLead.id)}
              >
                {t("actions.resend_link")}
              </Button>
            )}

            {detailLead && (
              <IconButton
                aria-label={t(
                  detailLead.starred ? "actions.unstar" : "actions.star",
                )}
                onClick={() => toggleStar(detailLead)}
              >
                {detailLead.starred ? (
                  <StarIcon color="warning" />
                ) : (
                  <StarBorderIcon />
                )}
              </IconButton>
            )}

            {detailLead && (
              <Button
                variant="outlined"
                onClick={() => toggleArchive(detailLead)}
              >
                {t(
                  detailLead.archived ? "actions.unarchive" : "actions.archive",
                )}
              </Button>
            )}

            {selectedLead && (
              <Button variant="contained" onClick={() => setRevising(true)}>
                {t("actions.edit_quote")}
              </Button>
            )}
          </div>
        </div>

        {selectedLead?.approvalStatus ===
          QuoteApprovalStatus.ChangesRequested &&
          selectedLead.changeRequestNote && (
            <Typography
              variant="body2"
              style={{
                padding: theme.spacing(0, 1, 1),
                color: theme.palette.text.secondary,
              }}
            >
              {t("detail.customer_asked", {
                note: selectedLead.changeRequestNote,
              })}
            </Typography>
          )}

        <Quote quoteData={quoteDataForDetail} readOnly height="100%" />

        {revising && selectedLead && (
          <ReviseQuoteModal
            quote={selectedLead}
            onSaved={() => refetchLeads({ requestPolicy: "network-only" })}
            onClose={() => setRevising(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Tabs {...tabData} />
      <div>
        <LeadTable
          leads={tabLeads}
          emptyText={t(EMPTY_TAB_TEXT[selectedTab])}
          onStageChange={handleStageChange}
          onAssigneeChange={handleAssigneeChange}
          onView={(id) => setSelectedLeadId(`${id}`)}
          onMessage={handleMessage}
          onRenew={handleRenew}
          onToggleStar={toggleStar}
          onToggleArchive={toggleArchive}
        />
      </div>
    </div>
  );
};

export default Leads;
